import { action, computed, makeObservable, observable } from 'mobx';
import moment from 'moment';

import { ModelArrayCast, ModelCast } from "shared/casts";
import { PermissionEnum } from "shared/enums";
import { userPreviewName } from "shared/utilities";
import { DepartmentBranchModel, IDepartmentBranchModel } from "./DepartmentBranchModel";

import { Model } from './Model';
import { IUserPersonalFieldModel, UserPersonalFieldModel } from "./UserPersonalFieldModel";
import { IUserPositionModel, UserPositionModel } from "./UserPositionModel";
import { IWidgetsModels, WidgetsModels } from "./WidgetsModels";

const withTimezone = (date?: string | null) => !!date && /([zZ]|[+-]\d{2}:?\d{2})$/.test(date.trim());

export interface IUserModel {
    id?: number;
    login?: string;
    name?: string;
    slug?: string;
    email?: string;
    image?: string;
    imageThumbnail?: string,
    imageAvatar?: string;
    phone?: string;
    mobile?: string;
    widgets?: null | {
        id: string,
        value: number
    }[];
    tinetsWidgets?:  null | IWidgetsModels[];
    ispringUserId?: string | null;
    ispringLogin?: string | null;
    outlookLogin?: string | null;
    birthdayAt?: string;
    birthdayIndex?: number;
    recentActivityAt?: string | null;
    hiredAt?: string | null;
    unavailableComment?: string | null,
    unavailableReason?: string | null,
    unavailableTo?: string | null,
    unavailableFrom?: string | null,
    unavailableUserId?: number | null,
    unavailableUser?: IUserModel | null,
    responsibilities?: string;
    userPositionNameOverride?: string;
    notificationUsersBirthdays?: number;
    saleStoreExternalId?: number | string | null
    saleRegionsCount?: number;
    saleStoresCount?: number;
    permissions?: string[];
    saleSeller?: { id: number } | null,
    userPosition?: IUserPositionModel | null;
    departmentBranch?: IDepartmentBranchModel | null;
    userPersonalFields?: IUserPersonalFieldModel[];
    management?: IUserModel[];
    colleagues?: IUserModel[];
    tinetsUserStat?: {
        ratingPosition: number;
        tinetsCount: number;
        level: number;
        completedTasksCount: number;
    } | null;
    tinetsTaskProgress?: {
        taskId: number;
        completedAt: string | null;
    }[];
}

export class UserModel extends Model implements IUserModel {
    casts = {
        userPosition: new ModelCast(UserPositionModel),
        departmentBranch: new ModelCast(DepartmentBranchModel),
        unavailableUser: new ModelCast(UserModel),
        userPersonalFields: new ModelArrayCast(UserPersonalFieldModel),
        management: new ModelArrayCast(UserModel),
        colleagues: new ModelArrayCast(UserModel),
    }
    id = 0;
    login = '';
    name = '';
    slug = '';
    email = '';
    responsibilities = '';
    birthdayAt = '';
    birthdayIndex = 0;
    saleRegionsCount = 0;
    saleStoresCount = 0;
    recentActivityAt: string | null = '';
    image = '';
    imageThumbnail = '';
    imageAvatar = '';
    phone = '';
    userPositionNameOverride = '';
    mobile = '';
    unavailableComment: string | null = null;
    unavailableReason: string | null = null;
    unavailableTo: string | null = null;
    unavailableFrom: string | null = null;
    unavailableUserId: number | null = null;
    hiredAt: string | null = null;
    widgets = null;
    tinetsWidgets: WidgetsModels[] | null = null;
    saleStoreExternalId = null;
    ispringUserId = null;
    ispringLogin = null;
    outlookLogin = null;
    saleSeller = null;
    permissions: string[] = [];
    notificationUsersBirthdays = 0;
    userPersonalFields: UserPersonalFieldModel[] = [];
    management: UserModel[] = [];
    colleagues: UserModel[] = [];
    unavailableUser: UserModel | null = null;
    userPosition: UserPositionModel | null = null;
    departmentBranch: DepartmentBranchModel | null = null;
    tinetsUserStat:IUserModel['tinetsUserStat'] = null;
    tinetsTaskProgress:IUserModel['tinetsTaskProgress'] = [];

    constructor(payload: IUserModel = {}) {
        super();

        makeObservable(this, {
            login: observable,
            name: observable,
            slug: observable,
            permissions: observable,
            birthdayAt: observable,
            birthdayIndex: observable,
            saleStoreExternalId: observable,
            recentActivityAt: observable,
            hiredAt: observable,
            departmentBranch: observable,
            image: observable,
            imageThumbnail: observable,
            imageAvatar: observable,
            phone: observable,
            responsibilities: observable,
            mobile: observable,
            unavailableComment: observable,
            unavailableReason: observable,
            unavailableTo: observable,
            unavailableFrom: observable,
            unavailableUserId: observable,
            email: observable,
            widgets: observable,
            tinetsWidgets: observable,
            saleRegionsCount: observable,
            unavailableUser: observable,
            saleStoresCount: observable,
            ispringUserId: observable,
            ispringLogin: observable,
            outlookLogin: observable,
            userPositionNameOverride: observable,
            notificationUsersBirthdays: observable,
            userPersonalFields: observable,
            colleagues: observable,
            management: observable,
            saleSeller: observable,
            userPosition: observable,
            tinetsUserStat: observable,
            tinetsTaskProgress: observable,
            update: action,
            birthDayAtMoment: computed,
            recentActivityAtMoment: computed,
            previewName: computed,
            unavailableToMoment: computed,
            unavailableFromMoment: computed,
            isAvailable: computed,
            unavailableText: computed,
            isAdmin: computed,
            isAvailableDashboard: computed,
            userPositionName: computed,
        });

        this.update(payload);
    }

    get arrayIdCompletedTask() {
        if (this.tinetsTaskProgress) {
            return this.tinetsTaskProgress
                .filter(item => !!item.completedAt)
                .map(item => item.taskId);
        } else {
            return [];
        }
    }

    get userLevel() {
        if (this.tinetsUserStat) {
            return this.tinetsUserStat.level
        } else {
            return 0;
        }
    }

    get userTinetsCount() {
        if (this.tinetsUserStat) {
            return this.tinetsUserStat.tinetsCount
        } else {
            return 0;
        }
    }

    get userRatingPosition() {
        if (this.tinetsUserStat) {
            return this.tinetsUserStat.ratingPosition
        } else {
            return 0;
        }
    }


    get userCompletedTasksCount() {
        if (this.tinetsUserStat) {
            return this.tinetsUserStat.completedTasksCount
        }
    }

    get recentActivityAtMoment() {
        if (!this.recentActivityAt) {
            return moment.invalid();
        }
        return withTimezone(this.recentActivityAt)
            ? moment(this.recentActivityAt)
            : moment.utc(this.recentActivityAt).local();
    }

    get birthDayAtMoment() {
        return moment(this.birthdayAt);
    }

    get unavailableToMoment() {
        if (!this.unavailableTo) {
            return moment().subtract('days', 1);
        }
        return moment(this.unavailableTo);
    }

    get unavailableFromMoment() {
        if (!this.unavailableFrom) {
            return moment().subtract('days', 1);
        }
        return moment(this.unavailableFrom);
    }

    can = (permission: string) => {
        if (this.permissions.filter(item => item === PermissionEnum.AdminPanelAdmin).length > 0) {
            return true;
        }
        return this.permissions.filter(item => item === permission || item.indexOf(permission) > -1).length > 0;
    };

    get previewName() {
        return userPreviewName(this.name);
    }

    get isAvailable() {
        const now = moment();
        if (now.isAfter(this.unavailableFromMoment) && now.isBefore(this.unavailableToMoment)) {
            return false;
        }
        return true;
    }

    get unavailableText() {
        if (this.isAvailable) {
            return '';
        }
        return `Недоступен до ${this.unavailableToMoment.format('DD MMMM')}`;
    }

    get userPositionName() {
        if (this.userPositionNameOverride) {
            return this.userPositionNameOverride;
        }
        if (this.userPosition?.name) {
            return this.userPosition.name;
        }
        return '';
    }

    get isAdmin() {
        return this.can(PermissionEnum.AdminPanelAdmin);
    }

    get isAvailableDashboard() {
        return this.can(PermissionEnum.WebDashboard);
    }

    is = (user: IUserModel | number | null | undefined) => {
        if (typeof user === 'number') {
            return this.id === user;
        }
        return this.id === user?.id;
    }
}
